package com.swetdot.swetgram.ui.groups

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.swetdot.swetgram.data.groups.GroupRepository
import com.swetdot.swetgram.domain.FieldEdit
import com.swetdot.swetgram.domain.Group
import com.swetdot.swetgram.domain.GroupEdit
import com.swetdot.swetgram.domain.GroupId
import com.swetdot.swetgram.domain.GroupPostingPolicy
import com.swetdot.swetgram.domain.GroupSettings
import com.swetdot.swetgram.domain.GroupSettingsEdit
import com.swetdot.swetgram.domain.GroupSettingsWrite
import com.swetdot.swetgram.domain.GroupVisibility
import com.swetdot.swetgram.protocol.Constraints
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

data class GroupSettingsForm(
    val name: String = "",
    val description: String = "",
    val username: String = "",
    val chatPosting: GroupPostingPolicy = GroupPostingPolicy.ALL_MEMBERS,
    val discoverPosting: GroupPostingPolicy = GroupPostingPolicy.ALL_MEMBERS,
)

data class GroupSettingsUiState(
    val group: Group? = null,
    val settings: GroupSettings? = null,
    val form: GroupSettingsForm = GroupSettingsForm(),
    val loadedOnce: Boolean = false,
    val saving: Boolean = false,
    val error: String? = null,
    val notice: String? = null,
) {
    val canEditIdentity: Boolean get() = group?.permissions?.canEditGroup == true
    val canEditPolicies: Boolean get() = group?.permissions?.canEditSettings == true
}

class GroupSettingsViewModel(
    private val groupId: GroupId,
    private val groups: GroupRepository,
) : ViewModel() {
    private val _state = MutableStateFlow(GroupSettingsUiState())
    val state: StateFlow<GroupSettingsUiState> = _state.asStateFlow()

    init { refresh() }

    fun refresh() {
        if (_state.value.saving) return
        viewModelScope.launch {
            try {
                val group = groups.group(groupId)
                val settings = groups.settings(groupId)
                _state.value = GroupSettingsUiState(
                    group = group,
                    settings = settings,
                    form = group.toForm(settings),
                    loadedOnce = true,
                )
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _state.value = _state.value.copy(
                    loadedOnce = true,
                    error = error.groupBanner("Could not load group settings."),
                )
            }
        }
    }

    fun onFormChange(form: GroupSettingsForm) {
        if (_state.value.saving) return
        _state.value = _state.value.copy(form = form, error = null, notice = null)
    }

    fun save() {
        val current = _state.value
        val group = current.group ?: return
        val settings = current.settings ?: return
        if (current.saving) return

        val form = current.form
        val name = form.name.trim()
        val description = form.description.trim()
        val username = form.username.trim().lowercase()
        val validation = when {
            name.isEmpty() -> "Enter a group name."
            name.length > GROUP_NAME_MAX -> "Group names can have at most $GROUP_NAME_MAX characters."
            description.length > GROUP_DESCRIPTION_MAX ->
                "Descriptions can have at most $GROUP_DESCRIPTION_MAX characters."
            group.visibility == GroupVisibility.PUBLIC && !Constraints.isUsernameValid(username) ->
                "Use 3–24 lowercase letters, numbers, or underscores for the username."
            else -> null
        }
        if (validation != null) {
            _state.value = current.copy(error = validation)
            return
        }

        val identity = if (current.canEditIdentity) GroupEdit(
            name = name.takeIf { it != group.name },
            description = when {
                description == group.description.orEmpty() -> FieldEdit.Unchanged
                description.isEmpty() -> FieldEdit.Clear
                else -> FieldEdit.Set(description)
            },
            username = username.takeIf {
                group.visibility == GroupVisibility.PUBLIC && it != group.publicAddress?.username
            },
        ) else GroupEdit()

        val policies = if (current.canEditPolicies) GroupSettingsEdit(
            version = settings.version,
            chatPosting = form.chatPosting.takeIf { it != settings.chatPosting },
            discoverPosting = form.discoverPosting.takeIf { it != settings.discoverPosting },
        ) else GroupSettingsEdit(version = settings.version)

        if (!identity.hasChanges && !policies.hasChanges) {
            _state.value = current.copy(notice = "Everything is already up to date.")
            return
        }

        _state.value = current.copy(saving = true, error = null, notice = null)
        viewModelScope.launch {
            try {
                var savedGroup = group
                var savedSettings = settings
                if (identity.hasChanges) {
                    savedGroup = groups.update(groupId, identity)
                    // Identity and posting policies are separate requests. Preserve the first
                    // successful response immediately, so a later policy error cannot put the
                    // old name or description back on screen.
                    _state.value = _state.value.copy(group = savedGroup)
                }
                if (policies.hasChanges) {
                    when (val result = groups.updateSettings(groupId, policies)) {
                        is GroupSettingsWrite.Updated -> savedSettings = result.settings
                        is GroupSettingsWrite.Stale -> {
                            _state.value = _state.value.copy(
                                group = savedGroup,
                                settings = result.current,
                                form = savedGroup.toForm(result.current),
                                saving = false,
                                error = "These posting settings changed elsewhere. Review the latest values and try again.",
                            )
                            return@launch
                        }
                    }
                }
                _state.value = _state.value.copy(
                    group = savedGroup,
                    settings = savedSettings,
                    form = savedGroup.toForm(savedSettings),
                    saving = false,
                    notice = "Group settings saved.",
                )
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _state.value = _state.value.copy(
                    saving = false,
                    error = error.groupBanner("Could not save group settings."),
                )
            }
        }
    }

    fun dismissMessage() {
        _state.value = _state.value.copy(error = null, notice = null)
    }
}

private fun Group.toForm(settings: GroupSettings) = GroupSettingsForm(
    name = name,
    description = description.orEmpty(),
    username = publicAddress?.username.orEmpty(),
    chatPosting = settings.chatPosting,
    discoverPosting = settings.discoverPosting,
)
